import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  IPlusDataV3Query,
  PosDataServiceMode,
  usePlusDataV3LazyQuery,
} from 'generated/graphql-gateway';
import { withLazySimpleCache } from 'state/graphql/with-simple-cache';
import { useServiceModeContext } from 'state/service-mode';

export type PosDataResults = {
  posData: Record<string, number> | null;
};

export type FetchPosDataFn = (args?: {
  storeNumber: string | null;
  isDelivery?: boolean;
}) => Promise<PosDataResults | null>;

export interface IUsePosData {
  data: PosDataResults;
  isLoading: boolean;
  refetch: FetchPosDataFn;
}

const useCachedPlusDataV3LazyQuery = withLazySimpleCache(usePlusDataV3LazyQuery);

type CachePOSData = {
  plusV3?: IPlusDataV3Query['plusV3'];
  data?: { plusV3: IPlusDataV3Query['plusV3'] };
};

export const usePosDataQuery = ({
  storeNumber,
  lazy = false,
}: {
  storeNumber: string | null;
  lazy?: boolean;
}): IUsePosData => {
  const { isDelivery } = useServiceModeContext();
  const [fetchPlusv3] = useCachedPlusDataV3LazyQuery();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [data, setData] = useState<PosDataResults>({
    posData: null,
  });

  const fetchPosData: FetchPosDataFn = useCallback(
    async args => {
      const transformedResponse: PosDataResults = { posData: null };

      const storeId = args?.storeNumber ?? storeNumber;
      if (!storeId) {
        return transformedResponse;
      }

      setIsLoading(true);
      const res = ((await fetchPlusv3({
        allowFromCache: true,
        variables: {
          storeId,
          serviceMode: isDelivery ? PosDataServiceMode.DELIVERY : PosDataServiceMode.PICKUP,
        },
      })) as unknown) as CachePOSData;
      setIsLoading(false);
      transformedResponse.posData = res.data?.plusV3 || res?.plusV3;
      setData(transformedResponse);
      return transformedResponse;
    },
    [isDelivery, storeNumber]
  );
  useEffect(() => {
    if (!lazy) {
      fetchPosData({
        storeNumber,
      });
    }
  }, [lazy, storeNumber, isDelivery, fetchPosData]);

  return useMemo(
    () => ({
      data,
      isLoading,
      refetch: fetchPosData,
    }),
    [data, isLoading, fetchPosData]
  );
};
